import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isFirstLogin, setIsFirstLogin] = useState(() => {
    return localStorage.getItem('isFirstLogin') === 'true';
  });

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Login with email/password
  const loginWithCredentials = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData, is_first_login } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('isFirstLogin', is_first_login ? 'true' : 'false');
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setIsFirstLogin(is_first_login || false);
    return { user: userData, isFirstLogin: is_first_login || false };
  };

  // Login with token directly (used after OTP verification)
  const login = (newToken, userData, firstLogin = false) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('isFirstLogin', firstLogin ? 'true' : 'false');
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setIsFirstLogin(firstLogin);
    return userData;
  };
  
  // Clear the first login flag (call after showing the welcome message)
  const clearFirstLogin = () => {
    localStorage.removeItem('isFirstLogin');
    setIsFirstLogin(false);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isFirstLogin');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setIsFirstLogin(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithCredentials, logout, isFirstLogin, clearFirstLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
